import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { addEvent, loadEvent, updateEvent, emptyEventsCache } from "../../../api/events";
import { searchCity } from '../../../api/citySearch';
import { toast, ToastContainer } from 'react-toastify';
import DatePicker from 'react-datepicker';
import ReactQuillNew from 'react-quill-new';
import { ROUTES } from "../../../config/routes";
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-quill-new/dist/quill.snow.css';

const CreateEvent = () => {
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    speaker: '',
    organizer: '',
    date: new Date(),  // Initialize with current date instead of null
    endDate: null,
    location: { cityName: '', postalCode: '', geo: '' },
    category: '',
    image: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventId, setEventId] = useState(null);
  const [citiesSuggestions, setCitiesSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const debounceTimeoutRef = useRef(null);

  const history = useHistory();
  const location = useLocation();

  const getQueryParams = (query) => {
    return new URLSearchParams(query);
  };

  const fetchEvent = useCallback(async (eid) => {
    try {
      setLoading(true);

      if (!eid) return;

      const event = await loadEvent(eid);
      setEventData({
        id: event.id,
        title: event.title || '',
        description: event.description || '',
        speaker: event.speaker || '',
        organizer: event.organizer || '',
        date: event.date instanceof Date ? event.date : new Date(),
        endDate: event.endDate instanceof Date ? event.endDate : null,
        location: event.location || { cityName: '', postalCode: '', geo: '' },
        category: event.category || '',
        image: event.image || null,
      });

      // log event data
      console.log('Event data:', event);
    } catch (error) {
      toast.error("Echec de chargement des données de l'évènement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const queryParams = getQueryParams(location.search);
    const eid = queryParams.get('eid');
    setEventId(eid);

    if (eid) {
      fetchEvent(eid);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [fetchEvent, location.search]);
  
  const handleChange = (e) => {
    const { id, value, files } = e.target;

    if (id === 'image') {
      setEventData((prevData) => ({
        ...prevData,
        image: files[0],
      }));
    } else if (id === 'location') {
      setEventData((prevData) => ({
        ...prevData,
        location: { cityName: value, postalCode: '', geo: '' },
      }));
    } else {
      setEventData((prevData) => ({
        ...prevData,
        [id]: value,
      }));
    }

    if (id === 'location') {
      if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchCitiesSuggestions(value);
    }, 500);
    }
  };

  const handleDateChange = (date, field) => {
    if (!date || isNaN(date.getTime())) {
      // Handle invalid date
      return;
    }
    
    // If setting end date, ensure it's not before start date
    if (field === 'endDate' && eventData.date && date < eventData.date) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }
    
    setEventData((prevData) => ({
      ...prevData,
      [field]: date,
    }));
  };

  const updateDescription = (description) => {
    setEventData((prevData) => ({
      ...prevData,
      description: description,
    }));
  };

  const fetchCitiesSuggestions = useCallback(async (query) => {
    if (query.length > 2) {
      setLoading(true);
      try {
       const citySuggestions = await searchCity(query);
        setCitiesSuggestions(citySuggestions);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setCitiesSuggestions([]);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      
      let result;

      if (eventId) {
        result = await updateEvent(eventData);
      } else {
        result = await addEvent(eventData);
      }

      if (result.success) {
        toast.success('Evènement édité avec succès!');
      } else {
        toast.error("Echec d'édition de l'évènement. Essayez plus tard.");
      }

      setEventData({
        title: '',
        description: '',
        speaker: '',
        organizer: '',
        date: new Date(),  // Reset to current date instead of undefined
        endDate: null,
        location: { cityName: '', postalCode: '', geo: '' },
        category: '',
        image: null,
      });

      // empty events data cache
      emptyEventsCache();

      setTimeout(() => {
        history.push(ROUTES.ALL_EVENTS.path);
      }, 2000);

    } catch (error) {
      toast.error('Failed to create event. Please try again.');
      console.error('Error creating event: ', error);
    } finally {
      setLoading(false);
    }
  };

  // Update handleBack to redirect to events and scroll to top
  const handleBack = () => {
    history.push(ROUTES.ALL_EVENTS.path);
    window.scrollTo(0, 0);
  };

  const handleLocationSelect = (location) => {
    setEventData((prevData) => ({
        ...prevData,
        location: location,
      }));
    setCitiesSuggestions([]);
  };

  const handleMouseEnter = (index) => {
    setActiveSuggestionIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveSuggestionIndex(-1);
  };

  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  return (
    <div className="contact-wrapper overflow-hidden">
      <ToastContainer />
      <div className="container pt-120 position-relative">
        <div className="row">
          <div className="col-lg-12">
            <h2>Edition évènement</h2>
            {error && <p className="error-message">{error}</p>}
            <form onSubmit={handleSubmit}>
              <div className="primary-input-group">
                <label htmlFor="title">Titre</label>
                <input
                  type="text"
                  id="title"
                  value={eventData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="primary-input-group">
                <label htmlFor="description">
                  Description
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-secondary ms-2" 
                    onClick={togglePreviewMode}
                  >
                    {isPreviewMode ? 'Mode HTML' : 'Mode Prévisualisation'}
                  </button>
                </label>
                {isPreviewMode ? (
                  <div 
                    className="preview-content border p-3" 
                    style={{ minHeight: '10em', overflowY: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: eventData.description }}
                  />
                ) : (
                  <ReactQuillNew
                    theme='snow'
                    value={eventData.description}
                    onChange={updateDescription}
                    style={{ height: '10em' }}
                  />
                )}
              </div>
              <div className="primary-input-group mt-5">
                <label htmlFor="speaker">Intervenant</label>
                <input
                  type="text"
                  id="speaker"
                  value={eventData.speaker}
                  onChange={handleChange}
                  autoComplete="off"
                  className="form-control"
                />
              </div>
              <div className="primary-input-group">
                <label htmlFor="organizer">Organisateur</label>
                <input
                  type="text"
                  id="organizer"
                  value={eventData.organizer}
                  onChange={handleChange}
                  autoComplete="off"
                  className="form-control"
                />
              </div>
              <div className="primary-input-group">
                <label htmlFor="date">Date et Heure de début</label>
                <div>
                  <DatePicker
                    selected={eventData.date}
                    onChange={(date) => handleDateChange(date, 'date')}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    className="form-control"
                    id="date"
                    required
                  />
                </div>
              </div>
              <div className="primary-input-group">
                <label htmlFor="endDate">Date et Heure de fin (optionnel)</label>
                <div>
                  <DatePicker
                    selected={eventData.endDate}
                    onChange={(date) => handleDateChange(date, 'endDate')}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    className="form-control"
                    id="endDate"
                    minDate={eventData.date}
                    placeholderText="Sélectionner une date de fin"
                  />
                </div>
              </div>
              <div className="primary-input-group">
                <label htmlFor="location">Localisation</label>
                <input
                  type="text"
                  id="location"
                  value={eventData.location.cityName ? `${eventData.location.cityName}${eventData.location.postalCode ? ` (${eventData.location.postalCode})` : ''}` : ''}
                  onChange={handleChange}
                  required
                  autoComplete='off'
                />
                {loading && <div className="loading">Chargement...</div>}
                {citiesSuggestions.length > 0 && !loading && (
                  <ul className="list-group suggestions-list">
                    {citiesSuggestions.map((suggestion, index) => (
                      <li
                        className={`list-group-item ${index === activeSuggestionIndex ? 'active' : ''}`}
                        key={index}
                        onClick={() => handleLocationSelect(suggestion)}
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {suggestion.postalCode ? `${suggestion.cityName} (${suggestion.postalCode})` : suggestion.cityName}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="primary-input-group">
                <label htmlFor="category">Categorie</label>
                <input
                  type="text"
                  id="category"
                  value={eventData.category}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="primary-input-group">
                <label htmlFor="image">Image</label>
                {eventData.image && (
                  <div className="mb-3">
                    <img
                      src={typeof eventData.image === 'string' ? eventData.image : URL.createObjectURL(eventData.image)}
                      alt="Article"
                      className="img-fluid mb-3"
                      style={{ maxHeight: '200px' }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleChange}
                />
              </div>
              
              <div className="submit-btn mt-2" >
                <button type="submit"
                  disabled={loading}
                  className="btn btn-primary">
                  {loading ? "En cours d'édition..." : 'Editer évènement'}
                </button>
              </div>
              
              <div className="submit-btn mt-2" >
                <button type="button" onClick={handleBack} className="btn btn-secondary">
                  <i className="bi bi-arrow-left" title="Retour aux évènements"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEvent;